import React, {useMemo} from 'react';
import {Navigate} from 'react-router-dom';
import {Title} from '../Title';
import MyAccounts from '../Accounts';
import Admin from './Admin';
import Download from '../Download/Download';
import Settings from '../Settings';
import Discover from '../Discover';
import {Markets} from '../Market/Markets';
import {Intent} from '../Intent/Intent';
import {Agents} from '../Agents/Agents';
import {AccountLists} from '../AccountLists/AccountLists';
import {CampaignChatsList} from '../ChatABM/CampaignChatsList';
import {CampaignChatSession} from '../ChatABM/CampaignChat';
import CallbackHandling from '../Integrations/CallbackHandling';
import {
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {useIsKeyplayAdmin} from '../../hooks/api/user';

type RouteConfig = {
  path: string;
  element: React.ReactElement;
  condition: boolean;
};

export const useOnboardedRoutes = (): RouteConfig[] => {
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const isFreemiumV3Customer = useIsFreemiumV3Customer();
  const isKeyplayAdmin = useIsKeyplayAdmin();

  return useMemo(
    () =>
      [
        {
          path: '/accounts/lists/*',
          element: (
            <Title title="Account Lists">
              <AccountLists />
            </Title>
          ),
          condition: true,
        },
        {
          path: '/accounts/*',
          element: (
            <Title title="My Accounts">
              <MyAccounts />
            </Title>
          ),
          condition: true,
        },
        {
          path: '/discover/*',
          element: (
            <Title title="Discover">
              <Discover />
            </Title>
          ),
          condition: !isFreemiumV3Customer,
        },
        {
          path: '/agents',
          element: (
            <Title title="Agents">
              <Agents />
            </Title>
          ),
          condition: !isFreeOrListBuilderCustomer,
        },
        {
          path: '/intent/*',
          element: (
            <Title title="Intent Models">
              <Intent />
            </Title>
          ),
          condition: true,
        },
        {
          path: '/markets/*',
          element: (
            <Title title="Markets">
              <Markets />
            </Title>
          ),
          condition: true,
        },
        {
          path: '/settings/*',
          element: (
            <Title title="Settings">
              <Settings />
            </Title>
          ),
          condition: true,
        },
        {
          path: '/download',
          element: <Download />,
          condition: true,
        },
        {
          path: '/oauth-callback',
          element: <CallbackHandling />,
          condition: true,
        },
        {
          path: '/admin/*',
          element: (
            <Title title="Admin">
              <Admin />
            </Title>
          ),
          condition: isKeyplayAdmin,
        },
        {
          path: '/chats',
          element: (
            <Title title="Campaign Chats">
              <CampaignChatsList />
            </Title>
          ),
          condition: isKeyplayAdmin,
        },
        {
          path: '/chats/:campaignChatId',
          element: <CampaignChatSession />,
          condition: isKeyplayAdmin,
        },
        {
          path: '*',
          element: (
            <Navigate
              to={
                isFreemiumV3Customer
                  ? '/accounts/intent'
                  : '/discover/recommended'
              }
            />
          ),
          condition: true,
        },
      ].filter((route) => route.condition !== false),
    [isFreeOrListBuilderCustomer, isFreemiumV3Customer, isKeyplayAdmin]
  );
};
